import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { useIsExpandedContext } from "components/BudgetBuilder/Overview/OverviewLineItemsList/OverviewLineItemsList";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Table from "components/Table";
import { budgetBuilderSelectors, tenantSelectors } from "store/selectors";
import {
	convertToLookupTable,
	formatNumberAsPercent,
	getFACostsByPeriod,
	getResearchTeamFACostsByPeriod,
} from "utils/budgetBuilder";

import styles from "./styles.css";


export const OverviewFAItems = () => {
	const { currencyFormatter } = useTenantFormatter();
	const { subawardCostCap } = useSelector(tenantSelectors.tenant);
	const sponsoredCostsByPeriod = useSelector(budgetBuilderSelectors.sponsoredCostsByPeriod);
	const researchTeamByPeriod = useSelector(budgetBuilderSelectors.researchTeamByPeriod);
	const periodRows = useSelector(budgetBuilderSelectors.periodRows);
	const { isExpandedState: { isFAExpanded }, isExpandedDispatch } = useIsExpandedContext();
	const setIsFAExpanded = useCallback(
		isExpanded => isExpandedDispatch({ type: "isFAExpanded", isExpanded }),
		[ isExpandedDispatch ],
	);
	const handleIsExpandedChange = isExpanded => {
		setIsFAExpanded(isExpanded);
	};

	// Initialize state
	useEffect(
		() => isExpandedDispatch({ type: "isFAExpanded", isExpanded: true }),
		[ isExpandedDispatch ]
	);

	const researchTeamFaCostByPeriod = useMemo(
		() => getResearchTeamFACostsByPeriod(researchTeamByPeriod, periodRows),
		[ periodRows, researchTeamByPeriod ]
	);

	const researchTeamTotalCost = useMemo(
		() => Object.values(researchTeamFaCostByPeriod).reduce((runningTotal, faCost) => runningTotal + faCost, 0),
		[ researchTeamFaCostByPeriod ]
	);

	const lineItemFaCostByPeriod = useMemo(
		() => getFACostsByPeriod(sponsoredCostsByPeriod, subawardCostCap, periodRows),
		[ periodRows, sponsoredCostsByPeriod, subawardCostCap ]
	);

	const lineItemsTotalCost = useMemo(
		() => Object.values(lineItemFaCostByPeriod).reduce((runningTotal, faCost) => runningTotal + faCost, 0),
		[ lineItemFaCostByPeriod ]
	);


	const faCostByPeriodEntries = useMemo(
		() => {
			let faCostByPeriod = {};
			for (let periodRow of periodRows) {
				let periodNumber = periodRow.periodNumber;
				let totalForPeriod = 0;
				totalForPeriod += lineItemFaCostByPeriod[periodNumber];
				totalForPeriod += researchTeamFaCostByPeriod[periodNumber];
				faCostByPeriod[periodNumber] = totalForPeriod;
			}

			return Object.entries(faCostByPeriod);
		},
		[ lineItemFaCostByPeriod, periodRows, researchTeamFaCostByPeriod ]
	);

	const totalCostString = useMemo(
		() => currencyFormatter(researchTeamTotalCost + lineItemsTotalCost),
		[ currencyFormatter, lineItemsTotalCost, researchTeamTotalCost ]
	);

	const faRatesByPeriod = useMemo(
		() => convertToLookupTable(periodRows, "periodNumber", "rateForPeriod"),
		[ periodRows ]
	);

	return (
		<>
			<Table.Row>
				<Table.Cell className={`${styles.cellAlignLeft} ${styles.expandCell}`}>
					<span>{"F&A"}</span>
					{isFAExpanded && <div className={styles.expandIcon} onClick={() => handleIsExpandedChange(!isFAExpanded)}>
						<i className="fa fa-caret-down" aria-hidden="true" />
					</div>}
					{!isFAExpanded && <div className={styles.expandIcon} onClick={() => handleIsExpandedChange(!isFAExpanded)}>
						<i className="fa fa-caret-right" aria-hidden="true" />
					</div>}
				</Table.Cell>
				{!isFAExpanded && <>
					{faCostByPeriodEntries.map(([ i, faCost ]) => (
						<Table.Cell className={styles.cellAlignRight} key={`summary-${i}`}>
							<span>{currencyFormatter(faCost)}</span>
						</Table.Cell>
					))}
					<Table.Cell className={styles.cellAlignRight}>
						<span className={styles.bold}>{totalCostString}</span>
					</Table.Cell>
				</>}
				{isFAExpanded && <>
					{periodRows?.map((period, p) => (
						<Table.Cell key={`period-${p}`}>
							<div className={styles.cellAlignSpread}>
								<span className={styles.bold}>{"Rate"}</span>
								<span className={styles.bold}>{"Amount"}</span>
							</div>
						</Table.Cell>
					))}
					<Table.Cell className={styles.cellAlignRight} />
				</>}
			</Table.Row>
			{(isFAExpanded) && <>
				<Table.Row key={"fa-research-team"}>
					<Table.Cell>{"Research Team"}</Table.Cell>
					{Object.entries(researchTeamFaCostByPeriod).map(([ periodNumber, faCost ]) => (
						<Table.Cell key={`research-team-${periodNumber}`}>
							<div className={styles.cellAlignSpread}>
								<span>{formatNumberAsPercent(faRatesByPeriod[periodNumber], "")}</span>
								<span>{currencyFormatter(faCost)}</span>
							</div>
						</Table.Cell>
					))}
					<Table.Cell className={styles.cellAlignRight}>
						<span className={styles.bold}>{currencyFormatter(researchTeamTotalCost)}</span>
					</Table.Cell>
				</Table.Row>
				<Table.Row key={"fa-line-items"}>
					<Table.Cell>{"Line Items"}</Table.Cell>
					{Object.entries(lineItemFaCostByPeriod).map(([ periodNumber, faCost ]) => (
						<Table.Cell key={`item-${periodNumber}`}>
							<div className={styles.cellAlignSpread}>
								<span>{formatNumberAsPercent(faRatesByPeriod[periodNumber], "")}</span>
								<span>{currencyFormatter(faCost)}</span>
							</div>
						</Table.Cell>
					))}
					<Table.Cell className={styles.cellAlignRight}>
						<span className={styles.bold}>{currencyFormatter(lineItemsTotalCost)}</span>
					</Table.Cell>
				</Table.Row>
			</>}
			<Table.Row>
				<Table.Cell className={`${styles.cellAlignLeft} ${styles.rowHeaderCell}`}>
					<span className={styles.bold}>{"Total F&A"}</span>
				</Table.Cell>
				{faCostByPeriodEntries.map(([ i, faCost ]) => (
					<Table.Cell className={`${styles.cellAlignRight} ${styles.rowHeaderCell}`} key={`total-${i}`}>
						<span className={styles.bold}>{currencyFormatter(faCost)}</span>
					</Table.Cell>
				))}
				<Table.Cell className={`${styles.cellAlignRight} ${styles.rowHeaderCell}`}>
					<span className={styles.bold}>{totalCostString}</span>
				</Table.Cell>
			</Table.Row>
		</>
	);
};

